import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Meta } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EventService } from '../shared/services/event.service';
import { EventNetworkingApiResponse, EventNetworkingParticipant } from '../shared/types/event-networking.types';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

@Component({
    selector: 'fp-event-networking-public',
    templateUrl: './event-networking-public.component.html',
    styleUrls: ['./event-networking-public.component.scss'],
    standalone: false
})
export class EventNetworkingPublicComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly linkedInClickedParticipantIds = new Set<string>();

  eventId = '';
  loading = true;
  errorMessage: string | null = null;
  eventData: EventNetworkingApiResponse | null = null;

  readonly pageRows = 12;
  first = 0;
  selectedInterest: string | null = null;
  interestOptions: { label: string; value: string }[] = [];

  constructor(
    private route: ActivatedRoute,
    private metaService: Meta,
    private translate: TranslateService,
    private eventService: EventService
  ) {
    this.metaService.addTag({ name: 'robots', content: 'noindex,nofollow' });
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map(params => params.get('eventId')),
        filter((id): id is string => !!id),
        distinctUntilChanged(),
        switchMap(eventId => {
          this.eventId = eventId;
          this.loading = true;
          this.errorMessage = null;
          this.eventData = null;
          this.linkedInClickedParticipantIds.clear();
          this.first = 0;
          this.selectedInterest = null;
          this.interestOptions = [];
          return this.eventService.getEventNetworking(eventId).pipe(
            finalize(() => {
              this.loading = false;
            }),
            catchError((err: HttpErrorResponse) => {
              const detail = err.error?.detail;
              this.errorMessage = typeof detail === 'string' ? detail : err.status ? `Error ${err.status}` : 'Request failed';
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(data => {
        this.eventData = data;
        this.interestOptions = this.eventData.all_interests;
      });
  }

  get pageTitle(): string {
    const t = this.eventData?.event_title?.trim();
    if (t) {
      return t;
    }
    return this.translate.instant('EVENT_NETWORKING_PUBLIC.DEFAULT_TITLE');
  }

  get filteredParticipants(): EventNetworkingParticipant[] {
    const list = this.eventData?.results ?? [];
    if (!this.selectedInterest) {
      return list;
    }
    const selected = this.selectedInterest.trim().toLowerCase();
    const selectedOptionLabel =
      this.interestOptions
        .find(o => o.value === this.selectedInterest)
        ?.label?.trim()
        .toLowerCase() ?? null;

    // Backend strings and dropdown values/labels can differ in casing/spaces, so compare in a normalized way.
    return list.filter(p => {
      const normalizedInterests = this.interestsList(p).map(i => i.trim().toLowerCase());
      return normalizedInterests.includes(selected) || (selectedOptionLabel ? normalizedInterests.includes(selectedOptionLabel) : false);
    });
  }

  get pagedParticipants(): EventNetworkingParticipant[] {
    return this.filteredParticipants.slice(this.first, this.first + this.pageRows);
  }

  get totalFiltered(): number {
    return this.filteredParticipants.length;
  }

  get showPaginator(): boolean {
    return this.totalFiltered > this.pageRows;
  }

  onInterestChange(): void {
    this.first = 0;
  }

  onPageChange(event: { first?: number }): void {
    this.first = event.first ?? 0;
  }

  getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /** Safe URL for `href` (Angular sanitizes `[href]` binding). Null when there is no link. */
  linkedinHref(participant: EventNetworkingParticipant): string | null {
    const url = participant.attendee_linkedin?.trim();
    if (!url) {
      return null;
    }
    return url.startsWith('http') ? url : `https://${url}`;
  }

  onLinkedInClick(participant: EventNetworkingParticipant): void {
    this.linkedInClickedParticipantIds.add(this.participantKey(participant));
  }

  isLinkedInClicked(participant: EventNetworkingParticipant): boolean {
    return this.linkedInClickedParticipantIds.has(this.participantKey(participant));
  }

  private participantKey(participant: EventNetworkingParticipant): string {
    return String(participant.id);
  }

  interestsList(p: EventNetworkingParticipant): string[] {
    return (p.attendees_interests ?? ([] as string[])).map(s => s.trim()).filter(Boolean);
  }

  onTakeTheSurvey(): void {
    window.open(`https://femmepalette.typeform.com/to/${this.eventData?.typeform_id}`, '_blank');
  }
}
